/**
 * An ID for all buttons possible in BottomMenu
 * - Can be used as key in an map
 * - Can be used for scroll into view
 * - Can be used to log user clicks to analytics
 *
 * **Remarks:** \
 * MenuIds are IDs for buttons that can open a menu, \
 * Menu's that can be opened via BottomMenu are included here (=most), \
 * this also can have ID's that don't open a menu, like PhotoZoneDelete
 */
export const BottomMenuIds = {
	AlternativeProducts: "alternative-products",
	BackgroundColor: "backgrounds-color",
	BackgroundPattern: "backgrounds-pattern",
	Backgrounds: "backgrounds",
	DateSelection: "date-selection",
	Designs: "designs",
	Events: "events",
	FontFamilies: "font-families",
	Layouts: "layouts",
	Map: "map",
	None: undefined,
	Options: "options",
	Photo: "photo",
	PhotoMap: "photo-map",
	PhotoBrightness: "photo-brightness",
	PhotoContrast: "photo-contrast",
	PhotoEditing: "photo-editing",
	PhotoFilters: "photo-filters",
	PhotoRotate: "photo-rotate",
	PhotoSaturation: "photo-saturation",
	PhotoSelection: "photo-selection",
	PhotoTransform: "photo-transform",
	PhotoZoneAdd: "photo-zone-add",
	PhotoZoneClear: "photo-zone-clear",
	PhotoZoneDelete: "photo-zone-delete",
	PhotoZoneDepth: "photo-zone-depth",
	PhotoZoom: "photo-zoom",
	ProductOptions: "product-options",
	Qr: "qr",
	QrAdd: "qr-add",
	QrDelete: "qr-delete",
	QrEdit: "qr-edit",
	Root: "",
	Symbols: "symbols",
	SymbolsList: "symbols-list",
	SymbolsZoneDelete: "symbols-zone-delete",
	SymbolsZoneDepth: "symbols-zone-depth",
	Text: "text",
	TextAlignment: "text-alignment",
	TextColor: "text-color",
	TextSize: "text-size",
	TextStyle: "text-style",
	TextValue: "text-value",
	TextZoneAdd: "text-zone-add",
	TextZoneClear: "text-zone-clear",
	TextZoneDelete: "text-zone-delete",
	TextZoneDepth: "text-zone-depth",
} as const;

export type BottomMenuId = (typeof BottomMenuIds)[keyof typeof BottomMenuIds];

export type RootBottomMenuIds =
	| typeof BottomMenuIds.Backgrounds
	| typeof BottomMenuIds.Designs
	| typeof BottomMenuIds.Events
	| typeof BottomMenuIds.Layouts
	| typeof BottomMenuIds.Options
	| typeof BottomMenuIds.Photo
	| typeof BottomMenuIds.Map
	| typeof BottomMenuIds.Qr
	| typeof BottomMenuIds.Symbols
	| typeof BottomMenuIds.Text;

type PhotosBottomMenuIds =
	| typeof BottomMenuIds.PhotoEditing
	| typeof BottomMenuIds.PhotoFilters
	| typeof BottomMenuIds.PhotoRotate
	| typeof BottomMenuIds.PhotoSelection
	| typeof BottomMenuIds.PhotoZoneAdd
	| typeof BottomMenuIds.PhotoZoneClear
	| typeof BottomMenuIds.PhotoZoneDelete
	| typeof BottomMenuIds.PhotoZoneDepth
	| typeof BottomMenuIds.PhotoMap;

export type PhotoEditBottomMenuIds =
	| typeof BottomMenuIds.PhotoBrightness
	| typeof BottomMenuIds.PhotoContrast
	| typeof BottomMenuIds.PhotoFilters
	| typeof BottomMenuIds.PhotoSaturation;

export type PhotoTransformBottomMenuIds = typeof BottomMenuIds.PhotoRotate;

export type TextBottomMenuIds =
	| typeof BottomMenuIds.FontFamilies
	| typeof BottomMenuIds.TextAlignment
	| typeof BottomMenuIds.TextColor
	| typeof BottomMenuIds.TextSize
	| typeof BottomMenuIds.TextStyle
	| typeof BottomMenuIds.TextValue
	| typeof BottomMenuIds.TextZoneAdd
	| typeof BottomMenuIds.TextZoneClear
	| typeof BottomMenuIds.TextZoneDelete
	| typeof BottomMenuIds.TextZoneDepth;

export type QrBottomMenuIds =
	| typeof BottomMenuIds.QrAdd
	| typeof BottomMenuIds.QrEdit
	| typeof BottomMenuIds.QrDelete;

export type BackgroundsBottomMenuIds =
	| typeof BottomMenuIds.BackgroundColor
	| typeof BottomMenuIds.BackgroundPattern;

type OptionsBottomMenuIds =
	| typeof BottomMenuIds.AlternativeProducts
	| typeof BottomMenuIds.DateSelection
	| typeof BottomMenuIds.ProductOptions;

export type SymbolsBottomMenuIds =
	| typeof BottomMenuIds.SymbolsList
	| typeof BottomMenuIds.SymbolsZoneDelete
	| typeof BottomMenuIds.SymbolsZoneDepth;

// type PRODUCT_OPTIONS_SUB_MENU_ID = typeof BottomMenuIds.ProductOptions;

const makePhotoMenu = <T extends PhotosBottomMenuIds>(subMenuId: T) =>
	`${BottomMenuIds.Photo}/${subMenuId}` as const;

const makePhotoEditMenu = <T extends PhotoEditBottomMenuIds>(subMenuId: T) =>
	`${BottomMenuIds.PhotoEditing}/${subMenuId}` as const;

const makeTextMenu = <T extends TextBottomMenuIds>(subMenu: T) =>
	`${BottomMenuIds.Text}/${subMenu}` as const;

const makeQrMenu = <T extends QrBottomMenuIds>(subMenu: T) =>
	`${BottomMenuIds.Qr}/${subMenu}` as const;

const makeBackgroundMenu = <T extends BackgroundsBottomMenuIds>(subMenu: T) =>
	`${BottomMenuIds.Backgrounds}/${subMenu}` as const;

const makeOptionsMenu = <T extends OptionsBottomMenuIds>(subMenu: T) =>
	`${BottomMenuIds.Options}/${subMenu}` as const;

const makeSymbolsMenu = <T extends SymbolsBottomMenuIds>(subMenu: T) =>
	`${BottomMenuIds.Symbols}/${subMenu}` as const;

export const BottomMenuPaths = {
	AlternativeProducts: makeOptionsMenu(BottomMenuIds.AlternativeProducts),
	BackgroundColor: makeBackgroundMenu(BottomMenuIds.BackgroundColor),
	BackgroundPattern: makeBackgroundMenu(BottomMenuIds.BackgroundPattern),
	Backgrounds: BottomMenuIds.Backgrounds,
	DateSelection: makeOptionsMenu(BottomMenuIds.DateSelection),
	Designs: `${BottomMenuIds.Root}/${BottomMenuIds.Designs}`,
	FontFamilies: makeTextMenu(BottomMenuIds.FontFamilies),
	Layouts: `${BottomMenuIds.Root}/${BottomMenuIds.Layouts}`,
	Options: BottomMenuIds.Options,
	Photo: BottomMenuIds.Photo,
	PhotoBrightness: makePhotoEditMenu(BottomMenuIds.PhotoBrightness),
	PhotoContrast: makePhotoEditMenu(BottomMenuIds.PhotoContrast),
	PhotoEditing: BottomMenuIds.PhotoEditing,
	PhotoFilters: makePhotoEditMenu(BottomMenuIds.PhotoFilters),
	PhotoSaturation: makePhotoEditMenu(BottomMenuIds.PhotoSaturation),
	PhotoSelection: makePhotoMenu(BottomMenuIds.PhotoSelection),
	PhotoTransform: BottomMenuIds.PhotoTransform,
	PhotoZoneDepth: makePhotoMenu(BottomMenuIds.PhotoZoneDepth),
	ProductOptions: makeOptionsMenu(BottomMenuIds.ProductOptions),
	Qr: BottomMenuIds.Qr,
	QrAdd: BottomMenuIds.QrAdd,
	QrDelete: makeQrMenu(BottomMenuIds.QrDelete),
	QrEdit: BottomMenuIds.QrEdit,
	Root: BottomMenuIds.Root,
	Symbols: BottomMenuIds.Symbols,
	SymbolsList: makeSymbolsMenu(BottomMenuIds.SymbolsList),
	SymbolsZoneDepth: makeSymbolsMenu(BottomMenuIds.SymbolsZoneDepth),
	Text: BottomMenuIds.Text,
	TextAlignment: makeTextMenu(BottomMenuIds.TextAlignment),
	TextColor: makeTextMenu(BottomMenuIds.TextColor),
	TextSize: makeTextMenu(BottomMenuIds.TextSize),
	TextStyle: makeTextMenu(BottomMenuIds.TextStyle),
	TextValue: BottomMenuIds.TextValue,
	TextZoneDepth: makeTextMenu(BottomMenuIds.TextZoneDepth),
} as const;

export type BottomMenuPath = (typeof BottomMenuPaths)[keyof typeof BottomMenuPaths];
